import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import ClientSignViewer from '../components/ClientSignViewer'
import DocumentDownloadButton from '../components/DocumentDownloadButton'
import { hasSignedDocument } from '../lib/documentDownloads'

export default function SignDocumentsPage() {
  const { profile, user } = useAuth()
  const [pending, setPending] = useState([])
  const [completed, setCompleted] = useState([])
  const [recentlyCompleted, setRecentlyCompleted] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [signingDoc, setSigningDoc] = useState(null)

  useEffect(() => { if (profile?.id || user?.id) loadDocuments() }, [profile?.id, user?.id])

  async function loadDocuments() {
    setLoading(true); setError('')
    setPending([]); setCompleted([]); setRecentlyCompleted(null)
    try {
    const uid = profile?.id || user?.id
    const { data: client, error: clientError } = await supabase
      .from('clients')
      .select('id')
      .eq('portal_user_id', uid)
      .maybeSingle()
    if (clientError) throw new Error(clientError.message)
    if (!client) throw new Error('Your portal account is not linked to a client record. Please contact your firm.')

    const scopedDocuments = () => supabase
      .from('documents')
      .select('id, client_id, file_name, file_type, file_path, signed_file_path, created_at, status, signing_status, requires_signature, uploaded_by, cpa_id, clients(full_name, business_name)')
      .eq('client_id', client.id)
      .eq('uploaded_by', 'cpa')
      .eq('requires_signature', true)
    // Keep completed history from consuming the pending request's result limit.
    const [pendingResult, completedResult] = await Promise.all([
      scopedDocuments().in('signing_status', ['pending', 'partial']).order('created_at', { ascending: false }),
      scopedDocuments().eq('signing_status', 'complete').order('created_at', { ascending: false }),
    ])
    if (pendingResult.error || completedResult.error) throw new Error((pendingResult.error || completedResult.error).message)
    setPending((pendingResult.data || []).filter(doc => ['pending', 'partial'].includes(doc.signing_status)))
    setCompleted((completedResult.data || []).filter(doc => doc.signing_status === 'complete'))
    } catch (err) { setError(err.message) }
    finally { setLoading(false) }
  }

  function onComplete(result, doc) {
    if (result.complete) {
      const completedDoc = {
        ...doc,
        status: 'signed',
        signing_status: 'complete',
        signed_file_path: result.signed_file_path || doc.signed_file_path || null,
      }
      setPending(prev => prev.filter(d => d.id !== doc.id))
      setCompleted(prev => [completedDoc, ...prev.filter(d => d.id !== doc.id)])
      setRecentlyCompleted(completedDoc)
    } else {
      setPending(prev => prev.map(d => d.id === doc.id ? { ...d, signing_status: 'partial' } : d))
    }
    setSigningDoc(null)
  }

  if (loading) return (
    <div style={{ display: 'grid', gap: 12 }}>
      {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 88 }} />)}
    </div>
  )

  return (
    <div className="sign-documents-page">
      <style>{`
        .sign-documents-page .sign-document-card { flex-wrap: wrap; }
        .sign-documents-page .sign-document-name { overflow-wrap: anywhere; }
        @media (max-width: 768px) {
          .sign-documents-page .sign-document-actions { flex-basis: 100%; flex-wrap: wrap; }
        }
      `}</style>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 24, fontWeight: 700, color: '#202326', margin: 0 }}>
          Sign Documents
        </h1>
        <p style={{ color: '#62615D', fontSize: 14, margin: '6px 0 0' }}>
          Review requests from your tax professional and download completed documents.
        </p>
      </div>

      {recentlyCompleted && (
        <div role="status" className="card" style={{ padding: '16px 20px', marginBottom: 20, background: '#F0FDF4', borderColor: '#BBF7D0', color: '#065F46', overflowWrap: 'anywhere' }}>
          <strong>{recentlyCompleted.file_name} is signed.</strong>
          <div style={{ marginTop: 4, fontSize: 13 }}>{hasSignedDocument(recentlyCompleted) ? 'Your signed PDF is ready to download under Completed documents below.' : 'Your signatures are saved. Check Completed documents below for the signed PDF.'}</div>
        </div>
      )}

      {error ? <div role="alert" className="card" style={{ padding: 24 }}><p>{error}</p><button className="btn btn-ghost" onClick={loadDocuments}>Try again</button></div> : pending.length === 0 ? (
        <div className="card" style={{ padding: completed.length ? '28px 24px' : '56px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h3 style={{ margin: '0 0 8px' }}>You're all caught up</h3>
          <p style={{ color: '#62615D', margin: 0, fontSize: 14 }}>No documents are waiting for your signature.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {pending.map(doc => (
            <div key={doc.id} role="button" tabIndex={0} onKeyDown={e => { if (['Enter', ' '].includes(e.key)) { e.preventDefault(); setSigningDoc(doc) } }} className="card animate-fadeUp sign-document-card" style={{ padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
              onClick={() => setSigningDoc(doc)}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.10)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = ''}>
              <div style={{ fontSize: 36, flexShrink: 0 }}>📄</div>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div className="sign-document-name" style={{ fontSize: 15, fontWeight: 700, color: '#202326', marginBottom: 3 }}>{doc.file_name}</div>
                <div style={{ fontSize: 12, color: '#62615D' }}>
                  From your tax professional · {new Date(doc.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              <div className="sign-document-actions" style={{ display: 'flex', alignItems: 'center', gap: 10, maxWidth: '100%' }}>
                {doc.signing_status === 'partial' && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '3px 8px', borderRadius: 20 }}>Partially signed</span>
                )}
                <span style={{ fontSize: 11, fontWeight: 700, color: '#065F46', background: '#D1FAE5', padding: '3px 8px', borderRadius: 20 }}>
                  {doc.signing_status === 'partial' ? 'Continue →' : 'Sign Now →'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {!error && completed.length > 0 && (
        <section aria-labelledby="completed-documents-heading" style={{ marginTop: 28 }}>
          <h2 id="completed-documents-heading" style={{ fontSize: 20, color: '#202326', margin: '0 0 6px' }}>Completed documents</h2>
          <p style={{ fontSize: 14, color: '#62615D', margin: '0 0 16px' }}>Your completed documents stay available here after signing.</p>
          <div style={{ display: 'grid', gap: 12 }}>
            {completed.map(doc => (
              <div key={doc.id} className="card sign-document-card" style={{ padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div aria-hidden="true" style={{ fontSize: 26, color: '#065F46', flexShrink: 0 }}>✓</div>
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <div className="sign-document-name" style={{ fontSize: 15, fontWeight: 700, color: '#202326', marginBottom: 3 }}>{doc.file_name}</div>
                  <div style={{ fontSize: 12, color: '#065F46' }}>All required signatures complete</div>
                </div>
                <div className="sign-document-actions" style={{ maxWidth: '100%' }}>
                  {hasSignedDocument(doc) ? (
                    <DocumentDownloadButton doc={doc} variant="signed" />
                  ) : (
                    <div style={{ maxWidth: 360, fontSize: 12, color: '#62615D' }}>
                      The signed PDF is not available yet. <button className="btn btn-ghost" onClick={loadDocuments} style={{ marginTop: 6 }}>Refresh documents</button>
                      <div style={{ marginTop: 6 }}>If it is still missing, contact your tax professional.</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {signingDoc && (
        <ClientSignViewer
          doc={signingDoc}
          onClose={() => setSigningDoc(null)}
          onComplete={(result) => onComplete(result, signingDoc)}
        />
      )}
    </div>
  )
}
